import { useState, useContext, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Modal, FilledInput, Typography, Button, FormGroup, FormLabel, FormControl, Box, Grid, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import themeColors from '../../assets/colors';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import AuthContext from '../../context';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: themeColors.background.default,
        padding: 20,
        width: '70%',
        borderRadius: 10
    },
    store: {
        marginLeft: 15,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    bottom: {
        display: 'flex',
        alignSelf: 'flex-end',
    }
}));

const defCarrierCatalog = { id: 0, name: 'Choose a carrier' };

const TicketCarrierCreate = ({ open, onClose }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const history = useHistory();
    const isMounted = useRef(false);
    const [state, setState] = useState({
        carriers: [defCarrierCatalog],
        ticketCarrier: {
            date: dayjs().format('YYYY-MM-DD'),
            amount: 0,
            container: '',
            carrier: defCarrierCatalog,
        }
    });

    useEffect(() => {
        if (open && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [open]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketCarrier/create');
        if (response.status === 200) {
            setState(prev => ({ ...prev, carriers: response.carriers }));
        } else {
            console.log('[TicketCarrierCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, ticketCarrier: { ...prev.ticketCarrier, [target.name]: target.value } }));
    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, ticketCarrier: { ...prev.ticketCarrier, [key]: value } }));

    const validateForm = () => {
        const { date, amount, carrier } = state.ticketCarrier;
        if (!date || Number(amount) <= 0 || Number(carrier?.id) < 1) {
            showToast('All fields are required', 'error');
            return false;
        }

        return true;
    }

    const store = async () => {
        const cond = validateForm();
        if (cond) {
            showLoader(true);
            const response = await post('/ticketCarrier/store', state.ticketCarrier);
            if (response.status === 200) {
                const { message, id } = response;
                history.push(`/ticket/ticketCarrier/${id}`);
                showToast(message);
            } else {
                console.log('[TicketCarrierCreate] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.root}
        >
            <div className={classes.container}>
                <Typography variant='h2'>{'Create carrier amount'}</Typography>

                <Grid container>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Date'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='date'
                                    placeholder='Date'
                                    name='date'
                                    value={state.ticketCarrier.date}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Amount'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='number'
                                    placeholder='Amount'
                                    name='amount'
                                    value={state.ticketCarrier.amount}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Container'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Container'
                                    name='container'
                                    value={state.ticketCarrier.container}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Carrier'}</FormLabel>
                            <Autocomplete
                                options={state.carriers}
                                value={state.ticketCarrier.carrier}
                                getOptionLabel={option => option?.name ?? ''}
                                getOptionSelected={(option, value) => option.id === value.id}
                                onChange={(e, value) => handleAutocomplete('carrier', value)}
                                renderInput={params => <TextField {...params} placeholder='Carrier' />}
                            />
                        </FormGroup>
                    </Grid>
                </Grid>

                <div className={classes.bottom}>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={onClose}
                    >
                        {'Cancel'}
                    </Button>
                    <Button
                        variant='contained'
                        className={classes.store}
                        onClick={store}
                    >
                        {'Store'}
                    </Button>
                </div>
            </div>
        </Modal>
    );

}

export default TicketCarrierCreate;